import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';
import { subscriptionAPI, authAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import {
  ChartLine,
  CheckCircle,
  ArrowLeft,
  Crown,
  Sparkle,
  Key,
  LockKey,
  CalendarBlank,
  Coins,
  CurrencyCircleDollar,
  Rocket,
  Buildings
} from '@phosphor-icons/react';

// Logo component
const FinanciALLogo = () => (
  <img 
    src="https://customer-assets-0z36b82j.emergentagent.net/job_1ce9899f-22e9-4951-9d4b-a91c40afd391/artifacts/ktwxigwh_Liquid%20Glass%20Logo.png.png" 
    alt="FinanciAL" 
    className="w-9 h-9 rounded-lg object-cover"
  />
);

const Billing = () => {
  const navigate = useNavigate();
  const { user, refreshUser } = useAuth();
  const [loading, setLoading] = useState(true);
  const [plans, setPlans] = useState([]);
  const [accessKey, setAccessKey] = useState('');
  const [redeemLoading, setRedeemLoading] = useState(false);
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  const currentTier = user?.subscription_tier || 'coin';

  useEffect(() => {
    fetchPlans();
    fetchSubscriptionStatus();
  }, []);

  const fetchPlans = async () => {
    try {
      const response = await subscriptionAPI.getPlans();
      setPlans(response.data);
    } catch (error) {
      console.error('Error fetching plans:', error);
      toast.error('Dështoi ngarkimi i planeve');
    } finally {
      setLoading(false);
    }
  };

  const fetchSubscriptionStatus = async () => {
    try {
      const response = await authAPI.getSubscriptionStatus();
      setSubscriptionStatus(response.data);
    } catch (error) {
      console.error('Error fetching subscription status:', error);
    }
  };

  const handleRedeemKey = async (e) => {
    e.preventDefault();
    if (!accessKey.trim()) {
      toast.error('Ju lutem vendosni kodin e aksesit');
      return;
    }

    setRedeemLoading(true);
    try {
      const response = await authAPI.redeemAccessKey(accessKey.trim());
      toast.success(response.data.message);
      setAccessKey('');
      
      // Refresh user data and subscription status
      if (refreshUser) {
        await refreshUser();
      }
      await fetchSubscriptionStatus();
      await fetchPlans();
    } catch (error) {
      console.error('Redeem error:', error);
      toast.error(error.response?.data?.detail || 'Dështoi aktivizimi i kodit');
    } finally {
      setRedeemLoading(false);
    }
  };

  const getPlanOrder = (planId) => {
    const order = { coin: 0, cashflow: 1, fortune: 2, enterprise: 3 };
    // Support legacy plan names
    const legacyOrder = { free: 0, basic: 1, professional: 2, enterprise: 3 };
    return order[planId] ?? legacyOrder[planId] ?? 0;
  };

  const isPlanHigher = (planId) => {
    return getPlanOrder(planId) > getPlanOrder(currentTier);
  };

  const isPlanLower = (planId) => {
    return getPlanOrder(planId) < getPlanOrder(currentTier);
  };

  // Albanian translations for plan names
  const planNameTranslations = {
    'Coin': 'Coin',
    'Cashflow': 'Cashflow',
    'Fortune': 'Fortune',
    'Enterprise': 'Enterprise',
    // Legacy names
    'Free': 'Coin',
    'Basic': 'Cashflow',
    'Professional': 'Fortune'
  };

  // Plan icons
  const getPlanIcon = (planId) => {
    const icons = {
      coin: Coins,
      cashflow: CurrencyCircleDollar,
      fortune: Rocket,
      enterprise: Buildings,
      // Legacy
      free: Coins,
      basic: CurrencyCircleDollar,
      professional: Rocket
    };
    return icons[planId] || Coins;
  };

  // Albanian translations for features
  const featureTranslations = {
    '1 Company': '1 Kompani',
    '5 Companies': '5 Kompani',
    '20 Companies': '20 Kompani',
    'Unlimited Companies': 'Kompani të Pakufizuara',
    '5 Documents': '5 Dokumente',
    '50 Documents': '50 Dokumente',
    '500 Documents': '500 Dokumente',
    'Unlimited Documents': 'Dokumente të Pakufizuara',
    'Basic Reports': 'Raporte Bazike',
    'Standard Reports': 'Raporte Standarde',
    'Advanced Reports': 'Raporte të Avancuara',
    'All Features': 'Të Gjitha Veçoritë',
    'Email Support': 'Mbështetje Email',
    'Priority Support': 'Mbështetje Prioritare',
    'Dedicated Support': 'Mbështetje Dedikuar',
    'Data Export': 'Eksport të Dhënash',
    'AI Insights': 'Njohuri AI',
    'API Access': 'Qasje API',
    'SLA': 'SLA',
    '1 Team Member': '1 Anëtar Ekipi',
    '5 Team Members': '5 Anëtarë Ekipi',
    '20 Team Members': '20 Anëtarë Ekipi',
    'Unlimited Team Members': 'Anëtarë Ekipi të Pakufizuar',
    '5 Workers': '5 Punëtorë',
    '50 Workers': '50 Punëtorë',
    '250 Workers': '250 Punëtorë',
    'Unlimited Workers': 'Punëtorë të Pakufizuar'
  };

  const translateFeature = (feature) => {
    return featureTranslations[feature] || feature;
  };

  const translatePlanName = (name) => {
    return planNameTranslations[name] || name;
  };

  const formatExpirationDate = (dateStr) => {
    if (!dateStr) return null;
    const date = new Date(dateStr);
    return date.toLocaleDateString('sq-AL', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  // Map current tier to display name
  const getDisplayTier = (tier) => {
    const mapping = {
      'free': 'Coin',
      'basic': 'Cashflow',
      'professional': 'Fortune',
      'coin': 'Coin',
      'cashflow': 'Cashflow',
      'fortune': 'Fortune',
      'enterprise': 'Enterprise'
    };
    return mapping[tier] || tier.charAt(0).toUpperCase() + tier.slice(1);
  };

  return (
    <div className="min-h-screen bg-[#F4F4F0]">
      {/* Header */}
      <header className="sticky top-0 z-40 w-full border-b border-[#1A3C34]/10 bg-white/95 backdrop-blur">
        <div className="flex h-16 items-center justify-between px-6">
          <div className="flex items-center gap-4">
            <Link to="/dashboard">
              <Button variant="ghost" size="sm" className="text-[#1A3C34]">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Kthehu te Paneli
              </Button>
            </Link>
          </div>
          <Link to="/dashboard" className="flex items-center gap-2">
            <FinanciALLogo />
            <span className="font-semibold text-lg text-[#1A3C34]">FinanciAL</span>
          </Link>
        </div>
      </header>

      <main className="p-6 max-w-5xl mx-auto">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-semibold text-[#1A3C34] mb-2">Planet e Abonimit</h1>
          <p className="text-[#1A3C34]/60">
            Aktivizoni planin tuaj me kodin e aksesit nga aplikacioni mobil.
          </p>
          <div className="flex flex-col items-center gap-2 mt-4">
            <Badge className="bg-[#1A3C34]" data-testid="current-plan-badge">
              Plani Aktual: {getDisplayTier(currentTier)}
            </Badge>
            {subscriptionStatus?.expires_at && (
              <div className="flex items-center gap-2 text-sm text-[#1A3C34]/60">
                <CalendarBlank className="w-4 h-4" />
                <span>Skadon: {formatExpirationDate(subscriptionStatus.expires_at)}</span>
              </div>
            )}
          </div>
        </div>

        {/* Access Key Input Section */}
        <Card className="mb-8 border-2 border-[#D4A373]/30 bg-gradient-to-r from-[#D4A373]/5 to-[#1A3C34]/5">
          <CardHeader className="pb-4">
            <CardTitle className="flex items-center gap-2 text-[#1A3C34]">
              <Key className="w-5 h-5 text-[#D4A373]" weight="fill" />
              Aktivizo me Kod Aksesi
            </CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleRedeemKey} className="flex flex-col sm:flex-row gap-3">
              <div className="flex-1">
                <Label htmlFor="access-key" className="sr-only">Kodi i Aksesit</Label>
                <Input
                  id="access-key"
                  data-testid="access-key-input"
                  type="text"
                  placeholder="Vendosni kodin tuaj (p.sh. FA-XXXX-XXXX-XXXX)"
                  value={accessKey}
                  onChange={(e) => setAccessKey(e.target.value.toUpperCase())}
                  className="bg-white border-[#1A3C34]/20 focus:border-[#D4A373] focus:ring-[#D4A373]/20"
                  disabled={redeemLoading}
                />
              </div>
              <Button
                type="submit"
                data-testid="redeem-key-btn"
                disabled={redeemLoading || !accessKey.trim()}
                className="bg-[#D4A373] hover:bg-[#D4A373]/90 text-white px-8"
              >
                {redeemLoading ? (
                  <>
                    <span className="animate-spin mr-2">⏳</span>
                    Duke aktivizuar...
                  </>
                ) : (
                  <>
                    <LockKey className="w-4 h-4 mr-2" />
                    Aktivizo Kodin
                  </>
                )}
              </Button>
            </form>
            <p className="text-xs text-[#1A3C34]/50 mt-3">
              Kodi aktivizohet vetëm një herë për llogari. Kontaktoni mbështetjen nëse keni probleme.
            </p>
          </CardContent>
        </Card>

        {loading ? (
          <div className="grid md:grid-cols-4 gap-6">
            {[...Array(4)].map((_, i) => (
              <Card key={i} className="border-[#1A3C34]/10">
                <CardContent className="p-6">
                  <Skeleton className="h-6 w-24 mb-4" />
                  <Skeleton className="h-10 w-32 mb-4" />
                  <Skeleton className="h-4 w-full mb-2" />
                  <Skeleton className="h-4 w-full mb-2" />
                  <Skeleton className="h-4 w-full" />
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          <div className="grid md:grid-cols-4 gap-6">
            {plans.map((plan, index) => {
              const normalizedPlanId = plan.plan_id === 'free' ? 'coin' : 
                                       plan.plan_id === 'basic' ? 'cashflow' :
                                       plan.plan_id === 'professional' ? 'fortune' : plan.plan_id;
              const normalizedCurrentTier = currentTier === 'free' ? 'coin' :
                                           currentTier === 'basic' ? 'cashflow' :
                                           currentTier === 'professional' ? 'fortune' : currentTier;
              
              const isCurrentPlan = normalizedPlanId === normalizedCurrentTier;
              const isHigherPlan = isPlanHigher(normalizedPlanId);
              const isLowerPlan = isPlanLower(normalizedPlanId);
              const PlanIcon = getPlanIcon(normalizedPlanId);
              const isPaidPlan = normalizedPlanId !== 'coin';

              return (
                <motion.div
                  key={plan.plan_id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  data-testid={`plan-card-${normalizedPlanId}`}
                >
                  <Card className={`h-full relative ${
                    isCurrentPlan ? 'border-[#1A3C34] border-2 shadow-xl' : 'border-[#1A3C34]/10'
                  }`}>
                    {isCurrentPlan && (
                      <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-4 py-1 bg-[#1A3C34] text-white text-xs font-medium rounded-full flex items-center gap-1">
                        <Crown className="w-3 h-3" weight="fill" />
                        Plani Aktual
                      </div>
                    )}
                    {normalizedPlanId === 'fortune' && !isCurrentPlan && (
                      <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-4 py-1 bg-[#D4A373] text-[#1A3C34] text-xs font-medium rounded-full flex items-center gap-1">
                        <Sparkle className="w-3 h-3" weight="fill" />
                        Më i Popullarizuar
                      </div>
                    )}
                    <CardContent className="p-6">
                      <div className="flex items-center gap-2 mb-2">
                        <PlanIcon className="w-6 h-6 text-[#D4A373]" weight="duotone" />
                        <h3 className="text-xl font-semibold text-[#1A3C34]">
                          {translatePlanName(plan.name)}
                        </h3>
                      </div>
                      <div className="flex items-baseline gap-1 mb-6">
                        {plan.price_monthly === 0 ? (
                          <span className="font-marketing text-3xl font-semibold text-[#2A9D8F]">
                            Falas
                          </span>
                        ) : (
                          <>
                            <span className="font-marketing text-3xl font-semibold text-[#1A3C34]">
                              ${plan.price_monthly}
                            </span>
                            <span className="text-[#1A3C34]/60">/muaj</span>
                          </>
                        )}
                      </div>
                      <ul className="space-y-2 mb-6">
                        {plan.features.map((feature) => (
                          <li key={feature} className="flex items-center gap-2 text-sm text-[#1A3C34]/70">
                            <CheckCircle className="w-4 h-4 text-[#2A9D8F] flex-shrink-0" weight="fill" />
                            {translateFeature(feature)}
                          </li>
                        ))}
                      </ul>
                      {isCurrentPlan ? (
                        <Button
                          className="w-full bg-[#1A3C34]/10 text-[#1A3C34] cursor-default"
                          disabled
                        >
                          Plani Aktual
                        </Button>
                      ) : isPaidPlan ? (
                        <div className="text-center">
                          <p className="text-xs text-[#1A3C34]/50 mb-2">
                            Përdorni kodin e aksesit për të aktivizuar
                          </p>
                          <Button
                            className="w-full bg-[#1A3C34]/10 text-[#1A3C34]"
                            disabled
                          >
                            <Key className="w-4 h-4 mr-2" />
                            Kërkon Kod
                          </Button>
                        </div>
                      ) : (
                        <Button
                          className="w-full bg-[#1A3C34]/10 text-[#1A3C34]"
                          disabled
                        >
                          Plan Bazë
                        </Button>
                      )}
                    </CardContent>
                  </Card>
                </motion.div>
              );
            })}
          </div>
        )}

        <div className="mt-12 text-center">
          <p className="text-[#1A3C34]/60 text-sm">
            Planet aktivizohen vetëm përmes kodeve të aksesit. Blini kodin nga aplikacioni ynë mobil.
          </p>
          <p className="text-[#1A3C34]/40 text-xs mt-2">
            Çdo kod është i vlefshëm për një llogari të vetme.
          </p>
        </div>
      </main>
    </div>
  );
};

export default Billing;
